/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	FileIcon,
	FileIconSimple,
	PreviewCard,
} from '@atlassian/conversation-assistant-ui-components';

import messages from './messages';

type FileCardProps = {
	fileName: string;
	isLoading?: boolean;
	error?: string;
	onRemove?: () => void;
	autoScroll?: boolean;
};

const styles = cssMap({
	fileCardBgWrapper: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.small'),
		display: 'flex',
	},
});

const FileCardBgWrappper = ({ children }: { children: React.ReactNode }) => {
	return <Box xcss={styles.fileCardBgWrapper}>{children}</Box>;
};

const getFileNameAndExtension = (fileName: string) => {
	const lastDotIndex = fileName.lastIndexOf('.');

	if (lastDotIndex === -1 || lastDotIndex === 0) {
		return { name: fileName, extension: '' };
	}

	return { name: fileName.slice(0, lastDotIndex), extension: fileName.slice(lastDotIndex + 1) };
};

export const FileCard = ({ fileName, onRemove, isLoading, error, autoScroll }: FileCardProps) => {
	const { formatMessage } = useIntl();
	const { name: fileNameWithoutExtension, extension } = getFileNameAndExtension(fileName);

	return (
		<PreviewCard
			autoScroll={autoScroll}
			label={fileNameWithoutExtension}
			subLabel={extension?.toUpperCase()}
			isLoading={isLoading}
			error={error}
			onRemove={onRemove}
			cardIcon={
				<FileCardBgWrappper>
					<FileIcon fileName={fileName} size="medium" />
				</FileCardBgWrappper>
			}
			subLabelIcon={
				<FileIconSimple size="small" fileName={fileName} color={token('color.icon.subtle')} />
			}
			errorLabel={formatMessage(messages.fileErrorIconLabel)}
			removeLabel={formatMessage(messages.fileCloseIconLabel)}
		/>
	);
};
